/**
 * TAXONOMIE UNIQUE du tracking marketing Propelsia — la source de vérité du
 * mapping « clé canonique → nom d'event chez chaque régie ».
 *
 * Principe (cf. docs/to_implement/tracking-unification-plan.md) :
 *   - un call site n'écrit JAMAIS un nom d'event de régie (`'Lead'`, `'Contact'`…) :
 *     il émet une clé canonique (`track('lp_cta_signup')`) et chaque destination lit
 *     SON nom dans la table ;
 *   - ajouter une régie = remplir une colonne ici + écrire un fournisseur — aucun
 *     call site à toucher ;
 *   - les allowlists des beacons publics sont DÉRIVÉES des flags (`mirrorable`,
 *     `preview_emittable`), plus jamais maintenues à la main.
 *
 * Consommée des deux côtés :
 *   - navigateur : `MarketingTracker.track(key)` (marketing-tracker.ts + destinations/) ;
 *   - serveur : `FunnelTrackingService.emitFunnelEvent(subject, key)` (backend).
 */

export type TrackingSurface = 'browser' | 'server' | 'both';

/** Nom d'un event chez une régie. `custom` = event non standard (`trackCustom` côté Pixel Meta). */
export interface DestinationEvent {
  name: string;
  custom?: boolean;
  /** Google Ads uniquement : libellé de conversion (`AW-XXX/label`). */
  conversion_label?: string;
}

export interface TrackingEventDef {
  /** Absent = l'event ne part pas chez cette régie. Tableau = plusieurs events pour un déclencheur. */
  meta?: DestinationEvent | ReadonlyArray<DestinationEvent>;
  tiktok?: DestinationEvent | ReadonlyArray<DestinationEvent>;
  google?: DestinationEvent | ReadonlyArray<DestinationEvent>;
  /**
   * Pixel OpenAI Ads (publicités ChatGPT). `custom: true` = event hors liste standard
   * OpenAI → envoyé en `measure('custom', …, { custom_event_name })` ; le nom doit alors
   * respecter leur contrat (minuscules, alphanum + `_`/`-`), garde dans taxonomy.spec.
   *
   * ⚠️ Seul le versant NAVIGATEUR est livré aujourd'hui (destination `openai-pixel`).
   * Les clés `surface: 'server'` portent déjà leur nom OpenAI pour que le branchement
   * de la CAPI OpenAI (`POST https://bzr.openai.com/v1/events`, clé serveur) ne soit
   * qu'un fournisseur de plus — mais aucun fournisseur serveur ne lit cette colonne.
   */
  openai?: DestinationEvent | ReadonlyArray<DestinationEvent>;
  /** `false` = pas de capture PostHog. Absent = la clé canonique elle-même. */
  posthog?: false | string;
  /** Goal Plausible. Absent = pas de goal (les pages vues sont natives au script). */
  plausible?: string;
  /** Émis une seule fois par sujet (garde `attribution.events_sent` + event_id déterministe). */
  once: boolean;
  /** D'où l'event peut légitimement être émis. */
  surface: TrackingSurface;
  /** Déjà capturé par posthog-js côté client → le serveur ne double pas la capture PostHog. */
  client_captured?: boolean;
  /** L'event DOIT porter `value` + `currency` dans ses props (cf. hasRequiredMonetaryProps). */
  monetary?: boolean;
  /** Émissible depuis le beacon public de la preview (`/public/lead_preview/:token/event`). */
  preview_emittable?: boolean;
  /**
   * Émissible depuis le beacon public anonyme `/public/utils/set-event` (miroir navigateur).
   * ⚠️ DISTINCT de `surface` : l'allowlist du beacon dérive de CE flag, jamais de `surface`.
   * `surface: 'both'` ne veut PAS dire injectable publiquement — `signup_completed` est 'both'
   * (pixel navigateur + serveur) mais son chemin navigateur passe par le pixel et le body du
   * signup, jamais par le beacon : le laisser mirrorable permettrait à n'importe quel bot de
   * fabriquer de fausses inscriptions chez TOUTES les régies d'un seul POST anonyme.
   */
  mirrorable?: boolean;
}

/**
 * La table. Contraintes de non-régression (audit 26/08) :
 *   - les noms PostHog des clés funnel restent les clés canoniques historiques
 *     (`lead_created`, `preview_viewed`…) — renommer casserait funnels/insights ;
 *   - aucun nom Meta existant n'est renommé, à UNE exception assumée : le CTA de la
 *     LP passe de `Lead` (collision avec `lead_qualified`) à l'event custom
 *     `LP_CTA_SIGNUP` — c'est l'objet même du correctif ;
 *   - `widget_opened` garde le goal Plausible historique `pulse_open` (continuité des
 *     dashboards) plutôt qu'un nouveau nom.
 */
const TRACKING_EVENTS_DEF = {
  // --- Vitrine / navigateur ---
  // Pages vues : natives chez Plausible (script) et PostHog ($pageview autocapture) —
  // un goal/une capture manuels ici seraient du double comptage.
  // ⚠️ PAS de colonne `tiktok` ici, volontairement : le pixel TikTok ne sait
  // émettre une page vue que par `ttq.page()`, qui ne porte pas d'`event_id` — un
  // `Pageview` server-side (Events API, event_id obligatoire) ne serait donc jamais
  // dédupliqué contre lui et doublerait toutes les pages vues. Le pixel est la seule
  // source TikTok des pages vues (cf. createTiktokPixelDestination).
  // ⚠️ PAS de colonne `openai` non plus, pour la raison INVERSE : le SDK OpenAI émet
  // `page_viewed` TOUT SEUL à l'`init` (une fois par chargement de page). Le déclarer
  // ici doublerait la page vue d'arrivée. Conséquence assumée : les navigations
  // App Router (SPA) ne sont pas comptées par OpenAI — ce sont les conversions qui
  // pilotent leurs campagnes, pas les pages vues.
  page_view: {
    meta: { name: 'PageView' },
    posthog: false,
    once: false,
    surface: 'browser',
    mirrorable: true,
  },
  // Choix de la bannière cookies — Plausible UNIQUEMENT (cookieless, donc mesurable
  // avant et sans consentement) : c'est ce qui donne le taux de consentement réel, là
  // où le ratio visiteurs PostHog ÷ visiteurs Plausible n'était qu'une estimation.
  // Props : `choix` (`accepte` | `refuse` | `partiel` | `implicite` — LP de campagne
  // sans bannière, test temporaire), `analytics` / `marketing` (`oui` | `non`),
  // `bandeau` (version du design, pour comparer les taux d'acceptation).
  // ⚠️ Jamais de colonne régie ni de capture PostHog ici : un refus
  // ne doit rien émettre vers un outil soumis au consentement.
  consent_choice: {
    plausible: 'Consentement',
    posthog: false,
    once: false,
    surface: 'browser',
  },
  // Clic CTA « Essayer gratuitement » de la LP. Ex-`Lead` (collision) → event custom.
  // ⚠️ Jamais `PRO_CTA` : nom v1 réservé aux interactions visiteurs sur les fiches clients.
  lp_cta_signup: {
    meta: { name: 'LP_CTA_SIGNUP', custom: true },
    tiktok: { name: 'ClickButton' },
    openai: { name: 'lp_cta_signup', custom: true },
    plausible: 'signup_click',
    once: false,
    surface: 'browser',
    mirrorable: true,
  },
  // Exposition à une variante d'A/B test de LP (cf. `website/src/lib/lp-experiments.ts`).
  // Props : `lp_experiment`, `lp_variant`, `lp_assignment`, `lp_angle`. Une fois par visite et par variante
  // (garde `sessionStorage` dans `LpExperimentTracker`). Plausible (cookieless, donc 100 % des
  // visiteurs : c'est le dénominateur du test) + PostHog. Aucune régie : ce n'est pas une conversion.
  lp_variant_viewed: {
    plausible: 'lp_variant_view',
    once: false,
    surface: 'browser',
  },
  // Ouverture de la bulle Pulse (clic bulle, deep-link `?pulse=open`, CTA de section).
  widget_opened: {
    meta: { name: 'WIDGET_OPENED', custom: true },
    openai: { name: 'widget_opened', custom: true },
    plausible: 'pulse_open',
    once: false,
    surface: 'browser',
    mirrorable: true,
  },
  // Premier message envoyé dans le widget. Partage volontairement le nom Meta `Contact`
  // ET un event_id dérivé du visitor token avec `lead_created` (serveur) : Meta déduplique
  // les deux remontées de la même réalité — cf. INTENTIONAL_ALIASES + buildWidgetContactEventId.
  widget_first_message: {
    meta: { name: 'Contact' },
    tiktok: { name: 'Contact' },
    // OpenAI n'a pas d'event standard « prise de contact » : custom, aliasé avec
    // `lead_created` comme chez Meta/TikTok.
    openai: { name: 'contact_started', custom: true },
    plausible: 'widget_message',
    once: false,
    surface: 'browser',
    mirrorable: true,
  },

  // --- Funnel lead (serveur, bus STR_EVENT_LEAD_KCHAT + beacon preview) ---
  lead_created: {
    meta: { name: 'Contact' },
    tiktok: { name: 'Contact' },
    openai: { name: 'contact_started', custom: true },
    once: true,
    surface: 'server',
  },
  preview_delivered: {
    meta: { name: 'PREVIEW_DELIVERED', custom: true },
    openai: { name: 'preview_delivered', custom: true },
    once: true,
    surface: 'server',
  },
  preview_viewed: {
    meta: { name: 'ViewContent' },
    tiktok: { name: 'ViewContent' },
    openai: { name: 'contents_viewed' },
    once: true,
    surface: 'browser',
    client_captured: true,
    preview_emittable: true,
  },
  preview_style_switched: { once: false, surface: 'browser', client_captured: true, preview_emittable: true },
  preview_booking_tested: { once: false, surface: 'browser', client_captured: true, preview_emittable: true },
  preview_gift_tested: { once: false, surface: 'browser', client_captured: true, preview_emittable: true },
  preview_register_clicked: {
    meta: { name: 'InitiateCheckout' },
    tiktok: { name: 'InitiateCheckout' },
    openai: { name: 'checkout_started' },
    once: true,
    surface: 'browser',
    client_captured: true,
    preview_emittable: true,
  },
  // ⚠️ Ne pas confondre : l'event STANDARD OpenAI `lead_created` (« lead soumis et
  // accepté ») correspond à notre `lead_qualified`, pas à notre clé `lead_created`
  // (simple prise de contact → custom `contact_started`).
  lead_qualified: {
    meta: { name: 'Lead' },
    tiktok: { name: 'SubmitForm' },
    openai: { name: 'lead_created' },
    once: true,
    surface: 'server',
  },
  lead_suspens: { once: false, surface: 'server' },
  lead_lost: { once: false, surface: 'server' },
  lead_human_needed: { once: false, surface: 'server' },
  lead_state_changed: { once: false, surface: 'server' },
  relance_sent: { once: false, surface: 'server' },

  // --- Conversions ---
  // Inscription : event standard `CompleteRegistration`, event_id pré-généré par
  // buildSignupAttribution() et partagé navigateur ↔ serveur (dédup Pixel ↔ CAPI).
  // (L'event custom v1 `SUBSCRIPTION` qui doublait l'inscription a été retiré :
  // aucune campagne n'a jamais optimisé dessus.)
  // PAS de `monetary` : l'essai est gratuit, `value` vaudrait 0 pour ~100 % des
  // conversions — la valeur vit sur `became_paid` (décision agence avant d'en ajouter une).
  // NON mirrorable malgré `surface: 'both'` — cf. le commentaire du type.
  // PAS de goal `plausible` : cette clé n'est émise côté navigateur que par
  // `fireSignupPixel` (app pro), or l'app n'embarque ni le script Plausible ni son
  // stub — `window.plausible?.()` y est un no-op muet, compté comme délivré. Le goal
  // déclaré ici n'a donc jamais été comptabilisé une seule fois. Le rétablir suppose
  // d'abord de charger Plausible sur `app.propelsia.ai`. Le funnel d'inscription
  // reste couvert par le goal `signup_click` de la LP (`lp_cta_signup`).
  signup_completed: {
    meta: { name: 'CompleteRegistration' },
    tiktok: { name: 'CompleteRegistration' },
    // `registration_completed` plutôt que `trial_started` : OpenAI dédupliquerait mal
    // deux events pour la même réalité, et c'est la création de compte qui est le
    // jalon commun à toutes les régies ici.
    openai: { name: 'registration_completed' },
    once: true,
    surface: 'both',
  },
  // Entreprise supplémentaire créée depuis un compte existant — PostHog uniquement :
  // ce n'est PAS une acquisition (aucune régie, aucun `signup_completed`), et les jalons
  // suivants de cette entreprise (`became_paid`…) ne partent pas non plus aux régies.
  company_created: { once: true, surface: 'server' },
  // Progression du wizard d'onboarding — PostHog uniquement (pilotage du
  // drop-off interne étape par étape, aucune optimisation pub là-dessus).
  // `onboarding_started` = intention posée (entrée effective dans le wizard) ;
  // `onboarding_step_completed` porte `step_id` + `step_order` en props.
  onboarding_started: { once: false, surface: 'server' },
  onboarding_step_completed: { once: false, surface: 'server' },
  // Jalons d'appropriation post-wizard (relance des essais, lot 0) — PostHog uniquement,
  // une fois par pro : ils nourrissent les étages du moteur de relance (§1 du plan) et le
  // funnel `signup_completed → onboarding_completed → étage 1 → … → became_paid`.
  //   - `availability_customized` : le pro touche ses horaires/dispos dans l'agenda,
  //     hors grille sectorielle posée par le wizard ;
  //   - `first_appointment_added` : premier RDV saisi par le pro (voie Résa, étage 3) ;
  //   - `website_published` : première publication du site (voie Site, étage 3) ;
  //   - `first_campaign_sent` : première campagne envoyée ou programmée (jalon d'activation,
  //     hors essai : l'envoi marketing est bloqué pendant l'essai).
  availability_customized: { once: true, surface: 'server' },
  first_appointment_added: { once: true, surface: 'server' },
  website_published: { once: true, surface: 'server' },
  first_campaign_sent: { once: true, surface: 'server' },
  // Relance d'essai envoyée (moteur trial_relance) — PostHog uniquement, une fois par moment :
  // props `moment`, `voie`, `stage`, `angle`, `segment`, `channels`, `content_key`.
  trial_relance_sent: { once: false, surface: 'server' },
  // Fin du wizard d'onboarding (dernière étape passée en `completed_steps`) :
  // jalon intermédiaire entre `signup_completed` et `became_paid`, demandé par
  // l'agence pour localiser où les inscrits s'arrêtent. Server-only : l'app n'a
  // de pixel que sur la page register, et l'émission dans
  // `OnboardingService.completeStep` est fiable (pas d'adblock, garde « une
  // fois » via le lead lié quand il existe).
  //
  // ⚠️ Couverture ASYMÉTRIQUE, à connaître avant de bâtir une audience dessus :
  // côté Meta/TikTok l'event ne part que pour les pros issus de Pulse. Le
  // consentement marketing et l'identifiant de clic ne sont pas persistés après
  // l'inscription, donc un pro sans lead n'a ni l'un ni l'autre et les deux
  // fournisseurs refusent l'event (cf. `MetaConversionProvider.send`). Côté
  // PostHog il couvre en revanche 100 % des inscrits.
  onboarding_completed: {
    meta: { name: 'ONBOARDING_COMPLETED', custom: true },
    tiktok: { name: 'ONBOARDING_COMPLETED', custom: true },
    openai: { name: 'onboarding_completed', custom: true },
    once: true,
    surface: 'server',
  },
  // Conversion payante — la SEULE clé `monetary: true` : c'est elle qui porte
  // `value`/`currency` et rend possibles l'enchère à la valeur et le ROAS.
  //
  // ⚠️ Émise par `ProSubscriptionService._createProSubscription` (création du 1er
  // abonnement = bascule essai → payant). Le bus d'état lead n'en est qu'un chemin de
  // SECOURS : il écoutait `LeadState.PAID`/`ACTIVE`, or aucun code ne pose jamais
  // `PAID` et `ACTIVE` n'est écrit qu'à la création du lead d'un pro — la conversion
  // n'était donc émise nulle part. Ne jamais re-brancher ce jalon sur une transition
  // d'état sans vérifier qu'un code la produit réellement.
  became_paid: {
    meta: { name: 'Subscribe' },
    tiktok: { name: 'Subscribe' },
    openai: { name: 'subscription_created' },
    once: true,
    surface: 'server',
    monetary: true,
  },

  // =========================================================================
  // ONBOARDING v4 (spec « Onboarding Propelsia » v4, section J). Convention : un
  // événement = un fait qui s'est produit, émis au moment où il se produit — jamais
  // un abandon, une errance, une inactivité ou un délai (états DÉRIVÉS, calculés
  // dans les marts depuis les horodatages). Noms snake_case objet_action, verbe au
  // passé. Les événements serveur sont la vérité ; les événements navigateur servent
  // au comportement. PostHog seulement, sauf les 5 conversions standard Meta
  // (ViewContent, Lead, CompleteRegistration, ONBOARDING_COMPLETED = lien vivant,
  // Subscribe) — toute autre clé n'a PAS de colonne régie.
  // Socle commun des props (section J) : anon_id puis professional_id, verticale,
  // situation, lp_variant, utm_*, meta_ad_id, device, viewport_w, langue,
  // session_id, jours_depuis_signup, trial_day. Jamais de donnée personnelle.
  // =========================================================================

  // --- Quiz d'avant-inscription (/start, LP) ---
  // Écran 1 affiché (une fois par session). Meta `ViewContent` = ouverture du quiz ;
  // alias déclaré avec `preview_viewed` (même événement standard, sujets disjoints).
  quiz_viewed: { meta: { name: 'ViewContent' }, once: false, surface: 'browser' },
  // Premier tap sur une carte métier. Props : metier.
  quiz_started: { once: false, surface: 'browser' },
  // Chaque réponse, tous écrans. Props : question, valeur(s), etape, temps_sur_ecran_ms.
  quiz_answered: { once: false, surface: 'browser' },
  // « Autre » : texte_saisi (≤ 80), verticale_deduite, confiance.
  quiz_other_metier: { once: false, surface: 'browser' },
  // La réponse change la suite (projet → 2 bis, « finalement je me lance »). Props : branche.
  quiz_branch: { once: false, surface: 'browser' },
  // Lien d'outil de réservation collé (écran 4). Props : outil_nom, domaine, reconnu.
  quiz_tool_link_pasted: { once: false, surface: 'browser' },
  // Écran 6 : métier retenu. Props : source = catalogue (référentiel) | libre (saisi tel quel),
  // activity_slug, verticale, via = pastille | recherche | libre.
  quiz_activity_source: { once: false, surface: 'browser' },
  // Écran 6 validé (« Voir ma démo »). Props : source, nb_prestations_proposees,
  // nb_prestations_detectees (gardées), nb_decochees, nb_ajoutees, niveau.
  quiz_activity_provided: { once: false, surface: 'browser' },
  // Navigation clavier sur ordinateur (écran 24).
  quiz_keyboard_used: { once: false, surface: 'browser' },
  // Fin de la conversation Pulse de l'écran 6 (serveur). Props : nb_questions, nb_sautees, vocal, duree_ms.
  pulse_quiz_session: { once: false, surface: 'server' },
  // Passerelle LP → app : sortie du navigateur intégré tentée. Props : app (instagram | facebook |
  // tiktok…), os, technique (intent | extbrowser | x-safari | sms), resultat.
  external_browser_forced: { once: false, surface: 'browser' },

  // --- Inscription / projet lointain ---
  // Écran 2 bis « Gardez-moi une place » : lead `DISTANT_PROJECT` avec date et e-mail (serveur).
  // Meta `Lead` = un contact saisi ; alias déclaré avec `lead_qualified` (sujets disjoints).
  // Props : date_projet (mois), a_email.
  lead_parked: { meta: { name: 'Lead' }, once: true, surface: 'server' },
  // « Finalement je me lance maintenant » ou reprise du quiz depuis le rappel. Props : depuis.
  lead_unparked: { once: false, surface: 'server' },
  // SMS / e-mail de lien d'accès envoyé à l'inscription (serveur). Props : canal, succes.
  magic_link_sent: { once: false, surface: 'server' },
  // E-mail d'une séquence admin envoyé à un LEAD (projet lointain). Pour un pro, voir `nudge_sent`.
  // Props : sequence, numero, template.
  sequence_email_sent: { once: false, surface: 'server' },

  // --- Guidage : démo, étapes, Pulse, import ---
  // `demo_generated` = `preview_delivered` ; `booking_link_live` = `onboarding_completed`
  // (+ prop `flow: 'v2'`) ; `site_published` = `website_published` ; `signup_submitted` =
  // `signup_submit_attempted` (télémétrie front) — pas de nouvelle clé pour ces quatre-là.
  // Écran 8 affiché. Props : niveau (minimal | complet), dans_webview.
  demo_viewed: { once: false, surface: 'browser' },
  // Clic sur un CTA de la démo. Props : cta (commencer | voir_site | tester_resa).
  demo_cta: { once: false, surface: 'browser' },
  // Brouillon de site généré depuis le brief (serveur). Props : source, template.
  site_draft_created: { once: false, surface: 'server' },
  // Écran d'une étape affiché. Props : etape (1–6), ecran.
  tunnel_step_started: { once: false, surface: 'browser' },
  // Métier donné à l'écran 9 bis (compte sans activité : inscription hors quiz). Props : via (pastille | recherche | libre), rattache.
  tunnel_activity_provided: { once: false, surface: 'browser' },
  // Écran 11 bis affiché. Props : depuis_quiz_phrase.
  about_prompt_viewed: { once: false, surface: 'browser' },
  // Transcription ou texte validé à l'écran 11 bis. Props : mode (vocal | texte), nb_mots, duree_vocal_s, chips_utilisees.
  about_captured: { once: false, surface: 'browser' },
  // CTA secondaire « affiner » : la page existante s'ouvre en mode tunnel. Props : etape, page.
  tunnel_step_refine_clicked: { once: false, surface: 'browser' },
  // Import de clientes lancé. Props : moyen (contact_picker | saisie_rapide | fichier).
  contacts_import_started: { once: false, surface: 'browser' },
  // Import terminé (serveur). Props : moyen, nb_importees, nb_doublons.
  contacts_import_completed: { once: false, surface: 'server' },
  // Extraction de l'offre lancée (flyer, lien outil, site). Props : source.
  offer_import_started: { once: false, surface: 'browser' },
  // Extraction terminée (serveur). Props : source, nb_prestations, confiance_moyenne.
  offer_import_completed: { once: false, surface: 'server' },
  // Conversation « montez mon offre » ouverte (écran 10 ; lot 5 : émis par l'app). Props : etape, depuis, nb_prestations_connues.
  pulse_offer_session_started: { once: false, surface: 'browser' },
  // Conversation quittée vers la liste ou l'étape (lot 5 : émis par l'app). Props : etape, nb_questions, duree_ms, abandon.
  pulse_offer_session_completed: { once: false, surface: 'browser' },
  // Réponse envoyée à Pulse dans le tunnel (lot 5). Props : etape, numero, vocal, chip, piece_jointe.
  pulse_question_answered: { once: false, surface: 'browser' },
  // Question posée par Pulse dans le tunnel. Props : question_id, etape.
  pulse_question_asked: { once: false, surface: 'server' },
  // « Je ne sais pas ». Props : question_id, etape.
  pulse_question_skipped: { once: false, surface: 'server' },
  // Texte rédigé par Pulse (description, accroche, à-propos). Props : type, version, garde.
  pulse_text_generated: { once: false, surface: 'server' },
  // Pulse a rempli la liste de l'écran 11 depuis l'écran 10 (rien d'écrit dans le catalogue). Props : nb_prestations, nb_a_confirmer.
  pulse_offer_proposed: { once: false, surface: 'server' },
  // Pulse a rempli l'écran lieux/horaires depuis le chat de l'étape 2 (rien d'écrit dans le compte). Props : champs.
  pulse_places_hours_proposed: { once: false, surface: 'server' },
  // Ligne de prestation modifiée dans l'écran 11. Props : champ.
  prestation_edited_inline: { once: false, surface: 'browser' },
  // Prestation retirée de la proposition (écran 11).
  prestation_removed: { once: false, surface: 'browser' },
  // Lieu créé (serveur). Props : kind (sur_place | deplacement | visio), rayon_km.
  place_created: { once: false, surface: 'server' },
  // Sous-étape du bandeau cochée par détection (serveur). Props : etape, sous_etape.
  substep_completed: { once: false, surface: 'server' },
  // Lien de reprise ouvert (serveur). Props : canal, etape.
  onboarding_resumed: { once: false, surface: 'server' },
  // Clic sur « reprendre » dans l'app (écran 13). Props : etape, champ.
  onboarding_resume_clicked: { once: false, surface: 'browser' },

  // --- Lien vivant et partage (étape 3) ---
  // Écran 15 affiché.
  victory_viewed: { once: false, surface: 'browser' },
  // Lien copié. Props : depuis (victoire | accueil | agenda).
  booking_link_copied: { once: false, surface: 'browser' },
  // Feuille de partage native confirmée (`navigator.share` résolu). Props : depuis, canal si connu.
  booking_link_shared: { once: false, surface: 'browser' },
  // Réservation test (`appointment.is_test`) créée (serveur).
  test_booking_made: { once: true, surface: 'server' },
  // « C'est fait » sur le lien en bio Instagram.
  instagram_bio_confirmed: { once: false, surface: 'browser' },

  // --- Tunnel (serveur = vérité) ---
  // Écran 16 affiché. Props : faites, ignorees.
  tunnel_progress_viewed: { once: false, surface: 'browser' },
  // Étape ouverte depuis la progression. Props : etape.
  tunnel_step_clicked: { once: false, surface: 'browser' },
  // Étape passée à « faite » (serveur). Props : etape, mode (guide | detecte), duree_ms.
  tunnel_step_completed: { once: false, surface: 'server' },
  // « Ignorer pour l'instant » (serveur, report posé). Props : etape, kind, raison.
  tunnel_step_skipped: { once: false, surface: 'server' },
  // 6/6 (serveur). Props : duree_totale_ms, ignorees, bonus.
  tunnel_completed: { once: true, surface: 'server' },
  // Écran 18 bis affiché. Props : duree_min, jours_depuis_signup, nb_ignores.
  tunnel_completed_screen_viewed: { once: false, surface: 'browser' },
  // Clic « Ouvrir mon espace » (18 bis).
  tunnel_completed_cta: { once: false, surface: 'browser' },
  // Écran 18 quater (ce qui est réglé) affiché. Props : depuis (fin | accueil | page), nb_reportes.
  recap_viewed: { once: false, surface: 'browser' },
  // « Modifier » sur une ligne du récap. Props : theme.
  recap_edit_clicked: { once: false, surface: 'browser' },
  // « En savoir plus » sur la ligne « pourquoi » d'une étape (volet B). Props : etape.
  step_why_expanded: { once: false, surface: 'browser' },
  // Écran 13 (reprise) affiché dans l'app. Props : etape, champ_manquant, jours_depuis_derniere_activite.
  tunnel_resume_viewed: { once: false, surface: 'browser' },
  // « Réserver un test moi-même » cliqué (écran 15) ; la réservation elle-même = `test_booking_made`.
  test_booking_started: { once: false, surface: 'browser' },
  // Plan §4.2 (lot 7) : le pro qui a déjà un site a choisi. Props : choix (propelsia | own_site).
  website_choice_made: { once: false, surface: 'server' },

  // --- Et maintenant (accueil après le tunnel) ---
  // Bloc principal affiché. Props : priorite (1–7).
  priority_viewed: { once: false, surface: 'browser' },
  // Props : priorite, depuis (bloc | carte | dock | sidebar).
  priority_clicked: { once: false, surface: 'browser' },
  // Priorité passée à « faite » (serveur). Props : priorite, jours_depuis_tunnel.
  priority_completed: { once: false, surface: 'server' },
  // Report rouvert depuis Et maintenant. Props : kind.
  deferred_item_resumed: { once: false, surface: 'browser' },
  // Brouillon d'étape sauvegardé (serveur, autosave). Props : etape, champ_manquant.
  tunnel_draft_saved: { once: false, surface: 'server' },
  // Report fermé par la chose faite (serveur). Props : kind, jours_depuis_report.
  deferred_item_completed: { once: false, surface: 'server' },

  // --- Site (étape 4) ---
  // Le pro demande un logo (provisoire ou généré). Props : mode.
  logo_requested: { once: false, surface: 'browser' },
  // Logo chargé (serveur). Props : source (upload | flyer | instagram).
  logo_uploaded: { once: false, surface: 'server' },
  // Logo provisoire généré (serveur). Props : style.
  logo_generated: { once: false, surface: 'server' },
  // Palette appliquée (serveur). Props : source (marque | secteur | flyer), palette.
  palette_applied: { once: false, surface: 'server' },
  // Mini-aperçu d'un style ouvert. Props : template.
  template_previewed: { once: false, surface: 'browser' },
  // Style appliqué (serveur). Props : template, mode (secteur_par_defaut | choisi).
  site_template_applied: { once: false, surface: 'server' },

  // --- Fiche Google et référencement (étape 5) ---
  // Clic « Se connecter avec Google ». Props : depuis.
  google_oauth_started: { once: false, surface: 'browser' },
  // Retour OAuth (serveur). Props : succes, nb_fiches.
  google_oauth_completed: { once: false, surface: 'server' },
  // Fiche reliée (serveur). Props : auto (une seule fiche) ou choisie.
  google_profile_linked: { once: true, surface: 'server' },
  // Push de l'existant vers la fiche (serveur). Props : champs[], erreurs.
  google_profile_synced: { once: false, surface: 'server' },
  // Pages locales / contenus SEO générés (serveur). Props : nb_villes, nb_pages.
  seo_auto_optimized: { once: false, surface: 'server' },
  // Partie Google du score recalculée (serveur). Props : score, delta.
  google_profile_score_changed: { once: false, surface: 'server' },
  // Demande d'avis envoyée (serveur). Props : canal, nb_destinataires.
  review_request_sent: { once: false, surface: 'server' },
  // Demande d'avis reportée (serveur, report `avis`).
  review_request_deferred: { once: false, surface: 'server' },
  // Actualité publiée sur la fiche (serveur).
  google_post_published: { once: false, surface: 'server' },
  // Avis reçu (serveur). Props : note.
  review_received: { once: false, surface: 'server' },
  // Réponse à un avis publiée (serveur). Props : par (pulse | pro).
  review_reply_published: { once: false, surface: 'server' },

  // --- Paiement (étape 6) ---
  // Clic « Connecter Stripe ». Props : depuis (tunnel | et_maintenant | paiements).
  stripe_connect_started: { once: false, surface: 'browser' },
  // `charges_enabled` passé à vrai (serveur, webhook).
  stripe_connect_completed: { once: true, surface: 'server' },
  // Retour de Stripe sans compte actif (page Comptes de paiements). Props : statut.
  stripe_connect_abandoned: { once: false, surface: 'browser' },
  // Règle suggérée par métier (serveur). Props : metier, mode, pourcentage.
  payment_mode_suggested: { once: false, surface: 'server' },
  // Règle enregistrée (serveur, `pending_payment_rule`). Props : mode, pourcentage, portee, suggestion_gardee.
  payment_mode_set: { once: false, surface: 'server' },
  // Règle appliquée aux prestations (serveur, Stripe actif). Props : nb_prestations.
  payment_mode_bulk_applied: { once: false, surface: 'server' },
  // Fiche prestation enregistrée avec un mode de paiement (serveur). Props : mode.
  prestation_saved: { once: false, surface: 'server' },

  // --- Espace pro (modes tunnel / libre / normal) ---
  // Pas de `page_viewed` / `screen_viewed` : `$pageview` PostHog est natif.
  // Accueil affiché. Props : mode (tunnel | libre | normal), priorite_principale.
  home_viewed: { once: false, surface: 'browser' },
  // Props : cta.
  home_cta_clicked: { once: false, surface: 'browser' },
  // Props : bloc (semaine | agenda | score | et_maintenant | ignores).
  home_block_clicked: { once: false, surface: 'browser' },
  // Bandeau d'étape replié. Props : etape, page.
  banner_collapsed: { once: false, surface: 'browser' },
  // Props : etape, page.
  banner_expanded: { once: false, surface: 'browser' },
  // Pulse ouvert avec contexte de page. Props : page, etape.
  pulse_opened_in_page: { once: false, surface: 'browser' },
  // Page ouverte par le menu en mode libre (hors guidage). Props : page, priorite_en_cours.
  free_browse: { once: false, surface: 'browser' },
  // Aperçu du gain affiché (mode libre). Props : page.
  gain_preview_viewed: { once: false, surface: 'browser' },
  // Clic sur le CTA de l'aperçu du gain. Props : page.
  gain_preview_cta: { once: false, surface: 'browser' },
  // Dock « priorité du jour » affiché. Props : priorite.
  dock_viewed: { once: false, surface: 'browser' },
  // Props : priorite.
  dock_clicked: { once: false, surface: 'browser' },
  // Carte « bravo » du dock : la priorité affichée vient d'être faite. Props : priorite.
  dock_celebrated: { once: false, surface: 'browser' },
  // Action refusée par une règle produit (image cassée, accroche vide, paiement sans Stripe…). Props : regle, page.
  blocked_action: { once: false, surface: 'browser' },

  // --- Suivi : coups de pouce (séquences admin), bilan, story ---
  // `trial_relance_sent` (ci-dessus) est conservé comme alias historique du moteur v1.
  // Relance envoyée par une séquence admin (serveur). Props : sequence, etape_sequence, canal
  // (email | sms | banniere | push), priorite.
  nudge_sent: { once: false, surface: 'server' },
  // Lien signé d'une relance ouvert (serveur). Props : sequence, etape_sequence, canal.
  nudge_clicked: { once: false, surface: 'server' },
  // La chose demandée par la relance a été faite dans les 48 h (serveur). Props : sequence, priorite.
  nudge_action_completed: { once: false, surface: 'server' },
  // « Votre semaine, en vrai » affichée. Props : mode (reel | objectifs).
  weekly_stats_viewed: { once: false, surface: 'browser' },
  // Story générée (serveur). Props : depuis (premiere_resa | dimanche).
  story_generated: { once: false, surface: 'server' },
  // Story partagée (feuille native résolue).
  story_shared: { once: false, surface: 'browser' },

  // --- Score de visibilité (interne, jamais de données Google) ---
  // Props : score, partie_pro, partie_google, delta.
  visibility_score_changed: { once: false, surface: 'server' },
} as const satisfies Record<string, TrackingEventDef>;

export type TrackingEventKey = keyof typeof TRACKING_EVENTS_DEF;

export const TRACKING_EVENT_KEYS = Object.keys(TRACKING_EVENTS_DEF) as ReadonlyArray<TrackingEventKey>;

export const TRACKING_EVENTS: Record<TrackingEventKey, TrackingEventDef> = TRACKING_EVENTS_DEF;

type TrackingDefs = typeof TRACKING_EVENTS_DEF;

/** Régies pour lesquelles la table porte un nom d'event. */
export type TrackingDestinationColumn = 'meta' | 'tiktok' | 'google' | 'openai';

/**
 * Alias de nom INTENTIONNELS, déclarés PAR régie — jamais globalement : un alias
 * toléré chez Meta (dédup par event_id prouvée, contrat CAPI) ne l'autorise pas
 * chez une autre régie tant que sa dédup n'a pas été validée séparément.
 * Toute autre collision de nom fait échouer le test 1 de taxonomy.spec.ts
 * (c'est le test qui aurait empêché la collision historique `Lead`).
 */
export const INTENTIONAL_ALIASES: Record<TrackingDestinationColumn, ReadonlyArray<ReadonlyArray<TrackingEventKey>>> = {
  // `widget_first_message` (navigateur) et `lead_created` (serveur) visent la même
  // réalité — un prospect entre en conversation — et partagent `Contact` + un
  // event_id dérivé du visitor token (cf. buildWidgetContactEventId).
  meta: [
    ['widget_first_message', 'lead_created'],
    // Événements STANDARD Meta partagés par deux déclencheurs à sujets DISJOINTS (un visiteur
    // du quiz n'est jamais un lead de preview, un projet lointain n'est jamais un lead Pulse
    // qualifié) : pas de double comptage possible, Meta reçoit le même standard pour la même
    // réalité « contenu vu » / « contact saisi » (spec onboarding v4, section J).
    ['quiz_viewed', 'preview_viewed'],
    ['lead_parked', 'lead_qualified'],
  ],
  // TikTok déduplique aussi par event_id (Events API) : le même partage y est déclaré,
  // à VALIDER en réel avant la première campagne TikTok (checklist lot 3).
  tiktok: [['widget_first_message', 'lead_created']],
  google: [],
  // Même réalité, même partage d'event_id que chez Meta — la dédup OpenAI se fait
  // aussi sur (pixel id, nom d'event, event_id). À VALIDER en réel quand la CAPI
  // OpenAI sera branchée (aujourd'hui seul le versant navigateur part).
  openai: [['widget_first_message', 'lead_created']],
};

/** Normalise `DestinationEvent | DestinationEvent[]` en tableau (vide si absent). */
export function toDestinationEvents(
  value: DestinationEvent | ReadonlyArray<DestinationEvent> | undefined,
): ReadonlyArray<DestinationEvent> {
  if (!value) return [];
  return Array.isArray(value) ? value : [value as DestinationEvent];
}

/**
 * Contrat monétaire : une clé `monetary: true` n'est émissible qu'avec `value` +
 * `currency` dans ses props. Utilisé par le tracker navigateur (drop) et testé
 * dans taxonomy.spec.ts. Côté serveur, `FunnelTrackingService` ENRICHIT d'abord
 * (montant relu depuis l'abonnement) avant d'appliquer le contrat.
 */
export function hasRequiredMonetaryProps(def: TrackingEventDef, props: Record<string, unknown>): boolean {
  if (def.monetary !== true) return true;
  return typeof props.value === 'number' && typeof props.currency === 'string' && props.currency.length > 0;
}

/**
 * Event_id partagé entre `widget_first_message` (navigateur) et `lead_created`
 * (serveur) : dérivé du visitor token, donc calculable indépendamment des deux
 * côtés — Meta déduplique les deux `Contact`. Tronqué à 64 (contrainte event_id).
 */
export function buildWidgetContactEventId(visitorToken: string): string {
  return `wfm-${visitorToken}`.slice(0, 64);
}

// ---------------------------------------------------------------------------
// Allowlists DÉRIVÉES des flags — plus rien n'est maintenu à la main.
// ---------------------------------------------------------------------------

/** Clés acceptées par le beacon public anonyme `/public/utils/set-event`. */
export const MIRRORABLE_EVENT_KEYS = TRACKING_EVENT_KEYS.filter(
  (key): boolean => TRACKING_EVENTS[key].mirrorable === true,
);

/** Clés émissibles depuis la preview (beacon token-scopé `/public/lead_preview/:token/event`). */
export type PreviewClientEventKey = {
  [K in TrackingEventKey]: TrackingDefs[K] extends { preview_emittable: true } ? K : never;
}[TrackingEventKey];

export const PREVIEW_CLIENT_EVENT_KEYS = TRACKING_EVENT_KEYS.filter(
  (key): key is PreviewClientEventKey => TRACKING_EVENTS[key].preview_emittable === true,
);
